import { Component } from 'preact'
import animateScrollTo from 'animated-scroll-to'
import { connect } from 'unistore/preact'

import prerendered from '../../../hoc/prerendered-content'
import Footer from '../../footer'
import s from './index.sass'
import PlanSection from './plan-section'
import MethodsSection from './methods-section'
import { isMobile } from '../../../helpers/devices'
import SummarySection from './summary-section'
import MembershipSummary from './membership-summary'
import {
  addCrumb,
  clearCrumbs
} from '../../header/actions'
import {
  setPaymentPlan,
  setPaymentMethod,
  updatePaymentInfo,
  getBrpProducts,
  redirectToPayment
} from './actions'
import { FullScreenLoader } from '../../loader'
import ErrorList from '../error-list'
import KeyCardSection from './key-card-section'
import { checkoutLoaded } from '../../../helpers/analytics'
import * as Sentry from '@sentry/browser'
import { PAYMENT_METHODS_DICTIONARY } from '../constants'
import guideInputs from '../../../../__guide-inputs.json'
import paymentInputs from '../../../../__payment-inputs.json'
import addOnsCopy from '../../../../__addons.json'
import NoMethodsSection from './no-methods'
import { setNoIndex } from '../../../helpers/meta-tags'

export const defaultScrollOptions = {
  verticalOffset: -(isMobile() ? 80 + 43 : 88 + 64),
  speed: 750
}

class SignUpPayment extends Component {
  state = {
    isLoadingProducts: true,
    errorLoadingProducts: false,
    isSubmitting: false,
    sections: {
      plan: {
        isCollapsed: true
      },
      options: {
        isCollapsed: true
      },
      keyCard: {
        isCollapsed: true
      },
      summary: {
        isCollapsed: true
      }
    }
  }

  references = {}

  componentDidMount () {
    setNoIndex()
    const { addCrumb, getBrpProducts } = this.props
    addCrumb('Betalning')

    getBrpProducts()
      .then(() => {
        const { paymentMethods } = this.props
        // Set plan depending on available methods...
        if (!paymentMethods.hasNonRecurring && paymentMethods.hasRecurring) {
          this.handleOnPlanInput('true')
          addCrumb('Betalningssätt')
        } else if (paymentMethods.hasNonRecurring && !paymentMethods.hasRecurring) {
          this.handleOnPlanInput('false')
          addCrumb('Betalningssätt')
        } else { // ...if it has both...
          addCrumb('Betalningsplan')
        }

        this.toggleNextSection(paymentMethods.hasAll ? 'plan' : 'options') // ... we leave it up to the user to input it.

        this.setState({
          isLoadingProducts: false,
          hasAllMethods: paymentMethods.hasAll,
          hasNoMethods: paymentMethods.hasNone
        })

        Sentry.addBreadcrumb({
          category: 'investigate',
          message: 'User landed on payment, products succeeded.',
          level: 'info'
        })
      })
      .catch((error) => {
        this.setState({
          isLoadingProducts: false,
          errorLoadingProducts: error
        })

        Sentry.addBreadcrumb({
          category: 'investigate',
          message: 'User landed on payment, products failed.',
          level: 'info'
        })
      })

    checkoutLoaded()
  }

  componentWillUnmount () {
    this.props.clearCrumbs()
  }

  setRef (key) {
    return (element) => { this.references[key] = element }
  }

  addCrumb (input) {
    const crumb = paymentInputs[input] && paymentInputs[input].name
    if (crumb && !this.props.breadcrumbs.includes(crumb)) this.props.addCrumb(paymentInputs[input].name)
  }

  toggleNextSection (next) {
    this.setState({
      sections: {
        ...this.state.sections,
        [next]: {
          isCollapsed: false
        }
      }
    })
  }

  handleOnPlanInput (value) { // Has it's own handler since all of the subsequent inputs depend on this. (In case the user changes it, we need to reset.)
    this.setState({
      sections: {
        ...this.state.sections,
        options: {
          isCollapsed: false
        },
        keyCard: {
          isCollapsed: true
        },
        summary: {
          isCollapsed: true
        }
      }
    }, () => {
      this.addCrumb('options')
      this.props.setPaymentPlan(value === 'true')
    })
  }

  handleOnMethodInput (value, periodsMonths) {
    this.setState({
      sections: {
        ...this.state.sections,
        keyCard: {
          isCollapsed: true
        },
        summary: {
          isCollapsed: true
        }
      }
    }, () => {
      this.addCrumb('keycard')
      this.props.setPaymentMethod(value, periodsMonths)
    })
  }

  handleOnCheckbox (currentInput, value, nextInput) { // Since it's a checkbox and not the only choice
    this.props.updatePaymentInfo(currentInput, value)

    if (nextInput) {
      this.addCrumb(nextInput)
      this.toggleNextSection(nextInput)
    }
  }

  proceedToNext (nextInput) {
    this.addCrumb(nextInput)
    this.toggleNextSection(nextInput)
  }

  scrollTo (key) {
    const preactRef = this.references[key]
    if (preactRef && preactRef.base) animateScrollTo(preactRef.base.offsetTop, defaultScrollOptions)
  }

  handleRedirect () {
    this.setState({
      isSubmitting: true,
      submitError: undefined
    })

    this.props.redirectToPayment().catch(error => {
      this.setState({
        isSubmitting: false,
        submitError: error
      })
    })
  }

  render ({
    paymentInfo,
    updatePaymentInfo,
    content
  }, {
    sections,
    isLoadingProducts,
    errorLoadingProducts,
    isSubmitting,
    hasAllMethods,
    hasNoMethods,
    submitError
  }) {
    return (
      <div class={s.view}>
        {isLoadingProducts || !paymentInputs
          ? <FullScreenLoader />
          : errorLoadingProducts
            ? (
              <div class={s.payment}>
                <ErrorList className={s.errorLoadingProducts} message={errorLoadingProducts.message} />
              </div>
            )
            : (
              <div class={s.payment}>
                <h1>{content.pageTitle}</h1>
                {hasNoMethods
                  ? <NoMethodsSection />
                  : (
                    <div>
                      <PlanSection
                        ref={this.setRef('plan')}
                        isCollapsed={sections.plan.isCollapsed}
                        title={paymentInputs.plan.title}
                        description={paymentInputs.plan.description}
                        onInput={(value) => this.handleOnPlanInput(value)}
                        inputs={paymentInputs.plan.inputs}
                      />

                      <MethodsSection
                        ref={this.setRef('options')}
                        hasAllMethods={hasAllMethods}
                        isCollapsed={sections.options.isCollapsed}
                        onExpand={() => this.scrollTo('options')}
                        title={paymentInputs.options.title}
                        description={paymentInputs.options.description}
                        proceedToNext={() => this.proceedToNext('keyCard')}
                        onInput={(value, periodsMonths) => this.handleOnMethodInput(value, periodsMonths)}
                        inputs={paymentInfo.isRecurring ? paymentInputs.options.recurringInputs : paymentInputs.options.cashInputs}
                      />

                      <KeyCardSection
                        ref={this.setRef('keyCard')}
                        isCollapsed={sections.keyCard.isCollapsed}
                        onExpand={() => this.scrollTo('keyCard')}
                        title={paymentInputs.keyCard.title}
                        description={paymentInputs.keyCard.description}
                        onCheckbox={(current, value, next) => this.handleOnCheckbox(current, value, next)}
                        inputs={paymentInputs.keyCard.inputs}
                        clubs={guideInputs.club.inputs}
                      />

                      <SummarySection
                        ref={this.setRef('summary')}
                        isCollapsed={sections.summary.isCollapsed}
                        onExpand={() => this.scrollTo('summary')}
                        inputName={paymentInputs.summary.name}
                        title={paymentInputs.summary.title}
                        paymentMethod={PAYMENT_METHODS_DICTIONARY[paymentInfo.method]}
                        skipCard={paymentInfo.skipCard}
                        error={submitError}
                        hasAccepted={paymentInfo.acceptTerms}
                        onClick={() => this.handleRedirect()}
                        onInput={updatePaymentInfo}
                        isSubmitting={isSubmitting}
                      />
                    </div>
                  )}

                <MembershipSummary inputs={guideInputs} addOnsCopy={addOnsCopy} />
              </div>
            )
        }
        <Footer />
      </div>
    )
  }
}

export const selectors = (state) => {
  const { breadcrumbs, paymentInfo, paymentMethods } = state
  return {
    breadcrumbs,
    paymentInfo,
    paymentMethods
  }
}

export default prerendered(connect(selectors, { addCrumb, clearCrumbs, setPaymentPlan, setPaymentMethod, updatePaymentInfo, getBrpProducts, redirectToPayment })(SignUpPayment))



// WEBPACK FOOTER //
// ./components/signup-flow/step-payment/index.js